import { useEffect, useState } from "react";
import { PageHero, PageLayout } from "../components/PageShell";
import { Figure } from "../components/Figure";
import type { Media } from "@zivin-labs/site-content";
import { LIBRARY_PHOTOS } from "../lib/campusLifeMedia";
import { docUrl, formatBytes } from "../lib/docUrl";
import { initials } from "../lib/staff";
import {
  LIBRARY_ABOUT,
  LIBRARY_BORROWING,
  LIBRARY_CHARGES,
  LIBRARY_COLLECTION,
  LIBRARY_COLLECTION_INTRO,
  LIBRARY_COMMITTEE,
  LIBRARY_COMMITTEE_HEADS,
  LIBRARY_DONTS,
  LIBRARY_DOS,
  LIBRARY_E_RESOURCES,
  LIBRARY_FEATURED_RESOURCES,
  LIBRARY_HOURS,
  LIBRARY_ID_CARD,
  LIBRARY_RULES,
  LIBRARY_SERVICES,
  LIBRARY_SERVICE_NOTES,
  LIBRARY_STAFF,
  LIBRARY_STATS,
  LIBRARY_USER_MANUAL,
  LIBRARY_ZONES,
  libraryPerson,
} from "../lib/library";

/** How many head-of-department portraits the collective seat shows before "+n". */
const HEADS_SHOWN = 5;

/** A 48px portrait, falling back to the person's monogram when we have none. */
function Avatar({ media, name }: { media?: Media; name: string }) {
  return (
    <div className="lib-avatar">
      <Figure
        media={media}
        fallbackLabel={initials(name)}
        aspect="1/1"
        className="lib-avatar-photo"
        sizes="48px"
      />
    </div>
  );
}

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "hours", label: "Hours" },
  { id: "collection", label: "Collection" },
  { id: "layout", label: "Layout" },
  { id: "membership", label: "Membership & borrowing" },
  { id: "services", label: "Services" },
  { id: "rules", label: "Rules & charges" },
  { id: "e-resources", label: "E-resources" },
  { id: "people", label: "Staff & committee" },
];

export default function Library() {
  const [activeId, setActiveId] = useState("overview");
  useEffect(() => {
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(
      (el): el is HTMLElement => el !== null,
    );
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -58% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <PageHero
        variant="hero-editorial"
        eyebrow="Library & Information Centre"
        title="A reading floor at the centre of the institute."
        lead="Three floors and two mezzanines in the Administrative Block — 2,661 square metres, seating for 600 readers, and a collection of over 1,50,000 volumes. Borrowing, book bank, reference, digital library and reprographics, run by nine staff."
        crumbs={[{ label: "Library" }]}
        // The home page hero already uses library-01 (the building exterior);
        // this picks a different one of the library's own published photos —
        // the reading hall — so it actually illustrates "seating for 600
        // readers" rather than repeating what the home page already shows.
        heroMedia={LIBRARY_PHOTOS[2]}
        image="CENTRAL LIBRARY · READING HALL"
      />

      <div className="library-detail">
        <PageLayout
          side={[
            {
              title: "On this page",
              items: SECTIONS.map((s) => ({
                label: s.label,
                to: `#${s.id}`,
                active: activeId === s.id,
              })),
            },
          ]}
        >
          {/* ─── Overview ─────────────────────────────── */}
          <h2 id="overview">About the library</h2>
          {LIBRARY_ABOUT.map((p, i) => <p key={i}>{p}</p>)}

          <div className="lib-stats">
            {LIBRARY_STATS.map((s) => (
              <div key={s.label}>
                <div className="num">{s.value}</div>
                <div className="lbl">{s.label}</div>
              </div>
            ))}
          </div>

          {/* ─── Hours ────────────────────────────────── */}
          <h2 id="hours">Working hours</h2>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Day</th>
                  <th scope="col">Borrowing</th>
                  <th scope="col">Reference</th>
                </tr>
              </thead>
              <tbody>
                {LIBRARY_HOURS.map((h) => (
                  <tr key={h.day}>
                    <th scope="row">{h.day}</th>
                    <td>
                      {h.borrowing.map((t) => <span key={t} className="lib-session">{t}</span>)}
                    </td>
                    <td>
                      {h.reference.map((t) => <span key={t} className="lib-session">{t}</span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ─── Collection ───────────────────────────── */}
          <h2 id="collection">Collection</h2>
          <p>{LIBRARY_COLLECTION_INTRO}</p>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Resource</th>
                  <th scope="col" className="num">Count</th>
                </tr>
              </thead>
              <tbody>
                {LIBRARY_COLLECTION.map((c) => (
                  <tr key={c.label}>
                    <th scope="row">{c.label}</th>
                    <td className="num">{c.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ─── Layout ───────────────────────────────── */}
          <h2 id="layout">Layout</h2>
          <p>What sits on each level of the library.</p>
          <div className="lib-zones">
            {LIBRARY_ZONES.map((z) => (
              <section key={z.title} className="lib-zone">
                <h3>{z.title}</h3>
                <ul>
                  {z.sections.map((sec) => <li key={sec}>{sec}</li>)}
                </ul>
              </section>
            ))}
          </div>

          {/* ─── Membership ───────────────────────────── */}
          <h2 id="membership">Membership &amp; borrowing</h2>
          <p>{LIBRARY_ID_CARD}</p>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Section</th>
                  <th scope="col">Members</th>
                  <th scope="col">Loan period</th>
                  <th scope="col" className="num">Books</th>
                  <th scope="col">Overdue</th>
                </tr>
              </thead>
              <tbody>
                {LIBRARY_BORROWING.map((b, i) => (
                  <tr key={i}>
                    <th scope="row">{b.books}</th>
                    <td>{b.members}</td>
                    <td>{b.duration}</td>
                    <td className="num">{b.count}</td>
                    <td>{b.overdue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ─── Services ─────────────────────────────── */}
          <h2 id="services">Services</h2>
          {LIBRARY_SERVICE_NOTES.map((p, i) => <p key={i}>{p}</p>)}
          <ul className="lib-services">
            {LIBRARY_SERVICES.map((s) => <li key={s}>{s}</li>)}
          </ul>

          {/* ─── Rules ────────────────────────────────── */}
          <h2 id="rules">Rules &amp; charges</h2>
          <ol className="lib-rules">
            {LIBRARY_RULES.map((r, i) => <li key={i}>{r}</li>)}
          </ol>

          <h3>Charges</h3>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Service</th>
                  <th scope="col">Charge</th>
                </tr>
              </thead>
              <tbody>
                {LIBRARY_CHARGES.map((c) => (
                  <tr key={c.label}>
                    <th scope="row">{c.label}</th>
                    <td>{c.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>In the reading hall</h3>
          <div className="lib-conduct">
            <section className="lib-conduct-col is-do">
              <div className="lib-conduct-head">Do</div>
              <ul>{LIBRARY_DOS.map((d) => <li key={d}>{d}</li>)}</ul>
            </section>
            <section className="lib-conduct-col is-dont">
              <div className="lib-conduct-head">Don't</div>
              <ul>{LIBRARY_DONTS.map((d) => <li key={d}>{d}</li>)}</ul>
            </section>
          </div>

          {/* ─── E-resources ──────────────────────────── */}
          <h2 id="e-resources">E-resources</h2>
          <p>
            Open-access databases, e-journals, newspapers and course platforms the library
            points readers to. Links are the institute's own and open on the publisher's site.
          </p>

          <div className="lib-er-featured">
            {LIBRARY_FEATURED_RESOURCES.map((r) => (
              <a key={r.url} className="lib-er-featured-card" href={r.url} target="_blank" rel="noreferrer noopener">
                <span className="lib-er-featured-tag">{r.tag}</span>
                <span className="lib-er-featured-name">{r.name}</span>
                <span className="lib-er-featured-blurb">{r.blurb}</span>
                <span className="lib-er-featured-cta">Open <span className="arr">↗</span></span>
              </a>
            ))}
          </div>

          {LIBRARY_E_RESOURCES.map((g) => (
            <section key={g.slug} className="lib-er">
              <div className="lib-er-head">
                <h3>{g.title}</h3>
                <span>{g.items.length}</span>
              </div>
              <ul className="lib-er-list">
                {g.items.map((r) => (
                  <li key={r.url + r.name}>
                    <a href={r.url} target="_blank" rel="noreferrer noopener">{r.name}</a>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {/* ─── People ───────────────────────────────── */}
          <h2 id="people">Staff &amp; committee</h2>
          <div className="lib-staff">
            {LIBRARY_STAFF.map((s) => (
              <div key={s.name} className="lib-person">
                <Avatar media={libraryPerson(s)?.photo} name={s.name} />
                <div className="lib-person-body">
                  <div className="lib-person-name">{s.name}</div>
                  <div className="lib-person-role">{s.designation}</div>
                  {(s.email || s.phone) && (
                    <div className="lib-person-contact">
                      {s.email && <a href={`mailto:${s.email}`}>{s.email}</a>}
                      {s.phone && <span>{s.phone}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <h3>Library committee</h3>
          <div className="lib-staff lib-committee">
            {LIBRARY_COMMITTEE.map((c) => {
              // The source names the seat, not the person. Keep the seat as the
              // heading — it is what the committee's constitution actually says
              // — and name whoever currently fills it underneath.
              const holder = libraryPerson(c);
              return (
                <div key={c.category} className="lib-person">
                  {c.allHeads ? (
                    /* One seat, many holders — a few faces and a count. */
                    <div className="lib-avatar-stack" aria-hidden="true">
                      {LIBRARY_COMMITTEE_HEADS.slice(0, HEADS_SHOWN).map((h) => (
                        <Avatar key={h.code} media={h.photo} name={h.name} />
                      ))}
                      {LIBRARY_COMMITTEE_HEADS.length > HEADS_SHOWN && (
                        <span className="lib-avatar-more">
                          +{LIBRARY_COMMITTEE_HEADS.length - HEADS_SHOWN}
                        </span>
                      )}
                    </div>
                  ) : (
                    <Avatar media={holder?.photo} name={holder?.name ?? c.category} />
                  )}
                  <div className="lib-person-body">
                    <div className="lib-person-name">{c.category}</div>
                    <div className="lib-person-role">{c.role}</div>
                    {holder && <div className="lib-person-contact">{holder.name}</div>}
                  </div>
                </div>
              );
            })}
          </div>

          <h3>Library user manual</h3>
          <a
            className="brochure-card reveal"
            href={docUrl(LIBRARY_USER_MANUAL.url)}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`Download the ${LIBRARY_USER_MANUAL.name} (PDF)`}
          >
            <span className="brochure-icon" aria-hidden="true">PDF</span>
            <span className="brochure-body">
              <span className="kicker">Library &amp; Information Centre</span>
              <span className="brochure-title">{LIBRARY_USER_MANUAL.name}</span>
              <span className="brochure-meta">
                Sections, services, borrowing rules &amp; conduct ·{" "}
                {formatBytes(LIBRARY_USER_MANUAL.bytes)} PDF
              </span>
            </span>
            <span className="brochure-cta">Download <span className="arr" aria-hidden="true">↓</span></span>
          </a>
        </PageLayout>
      </div>
    </>
  );
}
